/**
 * PostHog configuration per deployment variant (China web, global web, app WebView).
 * Uses POSTHOG_API_KEY for web; app WebView always uses the global PostHog project.
 */
import config from "@Config/index";
import getCookie from "helpers/getCookie";
import { appOS, isApp } from "hooks/useIsApp";

export type PostHogSource = "amber_china_website" | "amber_global_website" | "amber_app";

export type PostHogRuntimeConfig = {
  apiKey: string;
  host: string;
  source: PostHogSource;
};

const POSTHOG_CHINA_API_HOST = "https://tracking.amberstudent.cn";
const POSTHOG_GLOBAL_API_HOST = "https://posthog.amberstudent.com";

/** Global PostHog project key (app WebView + global web fallback). */
export const POSTHOG_GLOBAL_PROJECT_KEY = "phc_vCbyBuK48hfdALfigRCiaLunweodmY66WAput3j3zNhM";

export const getPostHogConfig = (): PostHogRuntimeConfig => {
  if (isApp()) {
    return {
      apiKey: POSTHOG_GLOBAL_PROJECT_KEY,
      host: POSTHOG_GLOBAL_API_HOST,
      source: "amber_app",
    };
  }

  if (config.IS_CHINA_APP) {
    return {
      apiKey: config.POSTHOG_API_KEY,
      host: POSTHOG_CHINA_API_HOST,
      source: "amber_china_website",
    };
  }

  return {
    apiKey: config.POSTHOG_API_KEY,
    host: POSTHOG_GLOBAL_API_HOST,
    source: "amber_global_website",
  };
};

const DESTINATION_COUNTRY_STORAGE_KEY = "destinationCountry";

/** ISO country from api.country.is (localStorage/cookie populated by fetchCountryData). */
export const getUserCountryForPostHog = (): string => {
  if (typeof window === "undefined") return "";
  return localStorage.getItem("country") || getCookie("userCountry") || "";
};

/** Destination country (search/property region), persisted from appData.countryCode. */
export const setDestinationCountryForPostHog = (countryCode?: string): void => {
  if (typeof window === "undefined" || !countryCode) return;
  localStorage.setItem(DESTINATION_COUNTRY_STORAGE_KEY, countryCode);
};

export const getDestinationCountryForPostHog = (): string => {
  if (typeof window === "undefined") return "";
  return localStorage.getItem(DESTINATION_COUNTRY_STORAGE_KEY) || "";
};

/** Shared event properties for PostHog captures (includes is_app when in app WebView). */
export const getPostHogEventContext = (): Record<string, string | boolean> => {
  const inApp = isApp();
  const userCountry = getUserCountryForPostHog();
  const countryCode = getDestinationCountryForPostHog();

  return {
    ...(inApp && {
      is_app: true,
      ...(appOS ? { app_platform: appOS } : {}),
    }),
    ...(config.IS_CHINA_APP && !inApp && { china_app: true }),
    ...(config.IS_WHITELABEL_APP && { is_whitelabel: true }),
    ...(userCountry && { user_source_country: userCountry }),
    ...(countryCode && { user_destination_country: countryCode }),
  };
};

/** Client-side PostHog is enabled for web and app WebView (not SSR). */
export const isPostHogEnabled = (): boolean => typeof window !== "undefined";

const POSTHOG_CONFIG = getPostHogConfig();

export default POSTHOG_CONFIG;
